import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { PaginatedDropdownLoadParams } from '@shared/utils/paginated-dropdown.util';
import { toSignal } from '@angular/core/rxjs-interop';
import { PermissionService } from '@app/core/services/permission.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { SpuService, SearchFormData } from '../services/spu.service';
import { SpuCategoryService } from '../services/spu-category.service';
import { SpuMaterialService } from '../services/spu-material.service';
import { extractCategoryIdFromTreeselect } from '@shared/utils/treeselect.util';
import {
  PackageSpuItem,
  PACKAGE_SPU_SEARCH_CONFIG,
  PACKAGE_SPU_COLUMNS,
  PACKAGE_SPU_BUTTON_CONFIG,
} from '../spu.config';

@Component({
  selector: 'app-package-spu',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './package-spu.component.html',
  styleUrl: './package-spu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageSpuComponent implements OnInit, OnDestroy {
  // Pagination
  first = 0;
  rows = 20;

  searchFormConfig = withPaginatedSearchFields(PACKAGE_SPU_SEARCH_CONFIG, {
    materialName: {
      loadPage: (params: PaginatedDropdownLoadParams) =>
        this.materialService.getMaterialOptionsPage({
          page: params.page,
          size: params.size,
          materialName: params.searchText || undefined,
        }),
    },
  });
  columns: TableColumn[] = this.getColumnsWithCustomTemplates();

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly cdr = inject(ChangeDetectorRef);

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.canPackageAction('BusinessGoodsSpuPackageView', 'product.product.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.canPackageAction('BusinessGoodsSpuPackageAdd', 'product.product.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.canPackageAction('BusinessGoodsSpuPackageEdit', 'product.product.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.canPackageAction('BusinessGoodsSpuPackage_Delete', 'product.product.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.canPackageAction('BusinessGoodsSpuPackage_Status', 'product.product.approve');
  });

  // Button configuration base
  private readonly primaryButtonBase = PACKAGE_SPU_BUTTON_CONFIG;

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedSpu: PackageSpuItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'disable' | 'enable' | 'delete' | null = null;

  // Data signals - following brand pattern
  spus = signal<PackageSpuItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    name: '',
    spuCode: '',
    type: '',
    categoryIdList: '' as string | string[],
    materialName: '',
    status: null as boolean | null,
    page: 1,
    size: 20,
  });

  // Private properties
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly spuService: SpuService,
    private readonly categoryService: SpuCategoryService,
    private readonly materialService: SpuMaterialService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
  ) {}

  private canPackageAction(packageCode: string, fallbackCode: string): boolean {
    return (
      this.permissionService.canDoAction(packageCode) ||
      this.permissionService.canDoAction(fallbackCode)
    );
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.loadDropdownOptions();
    this.loadSpus();
    this.setupRouterListener();

    // Subscribe to permissions loaded state to trigger change detection
    // This ensures computed signals re-evaluate when permissions are loaded
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (loaded) {
          // Trigger change detection to update computed signals
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Initialization methods
  private initializeComponent(): void {
    // Set SPU type for API calls
    this.spuService.setSpuType('package');
  }

  private loadDropdownOptions(): void {
    this.categoryService
      .getCategoryTree({
        statusFlag: undefined,
        subFlag: undefined,
        removeNotSubFlag: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categoryTree) => {
          this.searchFormConfig = this.searchFormConfig.map((field) =>
            field.key === 'categoryIdList'
              ? { ...field, treeOptions: categoryTree as any }
              : field,
          );
        },
        error: (error) => {
          console.error('Error loading category tree:', error);
        },
      });
  }

  // Load SPUs - following brand pattern
  private loadSpus(): void {
    this.loading.set(true);
    this.spuService.getSpus(this.searchParams()).subscribe({
      next: (response) => {
        this.spus.set(response.data as PackageSpuItem[]);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading SPUs:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.spu.messages.error.failed_to_load_products',
          ),
        });
      },
    });
  }

  private setupRouterListener(): void {
    // Listen for navigation events to refresh data when returning to this page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        // Check if we're navigating to the package SPU list page
        if (
          event.url.includes('/goods/spu/package') &&
          !event.url.includes('/add') &&
          !event.url.includes('/edit') &&
          !event.url.includes('/view')
        ) {
          // Refresh data when returning to list page
          this.loadSpus();
        }
      });
  }

  // Event handlers
  onSearch(searchData: SearchFormData): void {
    // Extract category ID from treeselect value
    const extractedCategoryId = extractCategoryIdFromTreeselect(
      searchData.categoryIdList,
    );
    const processedSearchData = {
      ...searchData,
      categoryIdList: extractedCategoryId || '', // Convert null to empty string
    };

    this.searchParams.set({
      ...this.searchParams(),
      ...processedSearchData,
      page: 1,
    });
    this.loadSpus();
  }

  onReset(): void {
    this.searchParams.set({
      name: '',
      spuCode: '',
      type: '',
      categoryIdList: '' as string | string[],
      materialName: '',
      status: null,
      page: 1,
      size: 20,
    });
    this.loadSpus();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.searchParams.set({
      ...this.searchParams(),
      page: event.first / event.rows + 1,
      size: event.rows,
    });
    this.loadSpus();
  }

  onAdd(): void {
    this.router.navigate(['/goods/spu/package/add']);
  }

  onView(spu: PackageSpuItem): void {
    this.router.navigate(['/goods/spu/package/view'], {
      queryParams: { id: spu.id },
    });
  }

  onEdit(spu: PackageSpuItem): void {
    this.router.navigate(['/goods/spu/package/edit'], {
      queryParams: { id: spu.id },
    });
  }

  onDelete(spu: PackageSpuItem): void {
    this.selectedSpu = spu;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.spu.messages.confirmation.delete_product',
      { name: spu.name },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onDisable(spu: PackageSpuItem): void {
    this.selectedSpu = spu;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.spu.messages.confirmation.deactivate_product',
      { name: spu.name },
    );
    this.currentAction = 'disable';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('goods.spu.actions.deactivate'),
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onEnable(spu: PackageSpuItem): void {
    this.selectedSpu = spu;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.spu.messages.confirmation.activate_product',
      { name: spu.name },
    );
    this.currentAction = 'enable';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('goods.spu.actions.activate'),
        action: 'confirm',
        severity: 'success',
      },
    ];
    this.showConfirmationDialog = true;
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      this.executeConfirmedAction();
    }
  }

  private executeConfirmedAction(): void {
    if (!this.selectedSpu) return;

    switch (this.currentAction) {
      case 'disable':
        this.confirmDisable();
        break;
      case 'enable':
        this.confirmEnable();
        break;
      case 'delete':
        this.confirmDelete();
        break;
    }
  }

  private confirmDisable(): void {
    if (this.selectedSpu) {
      this.spuService
        .updateStatus(this.selectedSpu.id, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.spu.messages.success.product_deactivated',
              ),
            });
            this.loadSpus(); // Refresh data
          },
          error: (error) => {
            console.error('Error deactivating package:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.spu.messages.error.failed_to_update_status',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private confirmEnable(): void {
    if (this.selectedSpu) {
      this.spuService
        .updateStatus(this.selectedSpu.id, true)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.spu.messages.success.product_activated',
              ),
            });
            this.loadSpus(); // Refresh data
          },
          error: (error) => {
            console.error('Error activating package:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.spu.messages.error.failed_to_update_status',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private confirmDelete(): void {
    if (this.selectedSpu) {
      this.spuService
        .deleteSpu(this.selectedSpu.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.spu.messages.success.product_deleted',
              ),
            });
            this.loadSpus(); // Refresh data
          },
          error: (error) => {
            console.error('Error deleting package:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.spu.messages.error.failed_to_delete_product',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedSpu = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Status methods
  getActiveSeverity(): 'success' {
    return 'success';
  }

  getInactiveSeverity(): 'secondary' {
    return 'secondary';
  }

  getActiveDisplayText(): string {
    return this.translateService.instant('goods.spu.status.active');
  }

  getInactiveDisplayText(): string {
    return this.translateService.instant('goods.spu.status.inactive');
  }

  getTypeDisplayText(type: string): string {
    return type === 'Physical Product'
      ? this.translateService.instant('goods.spu.types.physical_product')
      : this.translateService.instant('goods.spu.types.virtual_product');
  }

  private getColumnsWithCustomTemplates(): TableColumn[] {
    return PACKAGE_SPU_COLUMNS.map((column) => {
      if (column.prop === 'type') {
        return { ...column, type: 'custom' };
      }
      return column;
    });
  }

  getStatusIcon(status: boolean): string {
    return status
      ? 'pi pi-check-circle text-green-500'
      : 'pi pi-times-circle text-gray-500';
  }

  getStatusStyle(status: boolean): any {
    return status
      ? {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        }
      : {
          'background-color': '#f9fafb',
          color: '#6b7280',
          border: 'none',
        };
  }
}
